/**
 * Global control-plane settings (ADR-0040). Today: the runtime-tunable DEFAULT per-tenant
 * concurrency caps (workflow ingress + ETL). These are the instance-wide BACKSTOP a tenant
 * can't lift for itself; a per-tenant OVERRIDE lives on the plan entitlements (Piani).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Gauge, KeyRound, Loader2, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { platformApi, type PlatformSettingsCaps, type PlatformSmtpConfig } from "@/lib/platform";

const KEY = ["platform", "settings"];

export function PlatformSettingsPage() {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: KEY, queryFn: () => platformApi.getSettings() });

  const [form, setForm] = useState<PlatformSettingsCaps | null>(null);
  // Seed the editable form once the values arrive (and after a save refresh).
  useEffect(() => {
    if (settingsQ.data) {
      setForm({
        max_concurrent_workflow_runs: settingsQ.data.max_concurrent_workflow_runs,
        max_concurrent_etl_runs: settingsQ.data.max_concurrent_etl_runs,
      });
    }
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: (body: PlatformSettingsCaps) => platformApi.updateSettings(body),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });

  const defaults = settingsQ.data?.defaults;
  const dirty =
    form != null &&
    settingsQ.data != null &&
    (form.max_concurrent_workflow_runs !== settingsQ.data.max_concurrent_workflow_runs ||
      form.max_concurrent_etl_runs !== settingsQ.data.max_concurrent_etl_runs);

  return (
    <PageContainer>
      <PageHeader
        title="Impostazioni"
        description="Configurazione del control-plane, applicata a runtime separatamente a ogni tenant."
      />
      <PageContent className="max-w-2xl space-y-6">
        <section className="space-y-4 rounded-lg border border-border/60 bg-surface p-5">
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h3 className="text-sm font-medium">Limiti di concorrenza per tenant</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Quante esecuzioni un singolo tenant può avere in corso contemporaneamente (protezione
            "vicino rumoroso"). È un tetto di <strong>larghezza</strong> (non di durata) che vale{" "}
            <strong>separatamente per ogni tenant</strong>: non è un totale condiviso fra i tenant —
            con 50, ogni tenant arriva a 50 per conto suo, indipendentemente dagli altri. Questo è
            il valore di <strong>default</strong>; un singolo tenant può averlo sovrascritto dal suo{" "}
            <strong>piano</strong> (pagina Piani).
          </p>

          {settingsQ.isLoading || !form ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
            </div>
          ) : (
            <div className="space-y-4">
              <CapField
                label="Workflow in esecuzione (da ingress)"
                hint={`Default: ${defaults?.max_concurrent_workflow_runs ?? "—"}`}
                value={form.max_concurrent_workflow_runs}
                onChange={(v) => setForm({ ...form, max_concurrent_workflow_runs: v })}
              />
              <CapField
                label="Pipeline ETL in esecuzione"
                hint={`Default: ${defaults?.max_concurrent_etl_runs ?? "—"}`}
                value={form.max_concurrent_etl_runs}
                onChange={(v) => setForm({ ...form, max_concurrent_etl_runs: v })}
              />
              <div className="flex items-center gap-3 pt-1">
                <Button
                  size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() => form && save.mutate(form)}
                >
                  {save.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
                  Salva
                </Button>
                {save.isSuccess && !dirty && <span className="text-sm text-success">Salvato.</span>}
              </div>
            </div>
          )}
        </section>

        <SmtpSection />

        <CentralTokenSection />
      </PageContent>
    </PageContainer>
  );
}

/** Central's directory service token (ADR-0085). Only the HASH is stored server-side
 * (platform_settings), so the plaintext appears exactly ONCE at mint — the operator
 * copies it into Central's env (`CENTRAL_CONTROL_SERVICE_TOKEN`) and restarts it.
 * Rotating kills the previous token; revoking degrades Central's system acts. */
function CentralTokenSection() {
  const qc = useQueryClient();
  const TOKEN_KEY = ["platform", "central-token"];
  const statusQ = useQuery({
    queryKey: TOKEN_KEY,
    queryFn: () => platformApi.getCentralToken(),
  });

  const [minted, setMinted] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mint = useMutation({
    mutationFn: () => platformApi.mintCentralToken(),
    onSuccess: (data) => {
      setMinted(data.token);
      setCopied(false);
      qc.invalidateQueries({ queryKey: TOKEN_KEY });
    },
  });
  const revoke = useMutation({
    mutationFn: () => platformApi.revokeCentralToken(),
    onSuccess: () => {
      setMinted(null);
      qc.invalidateQueries({ queryKey: TOKEN_KEY });
    },
  });

  const configured = statusQ.data?.configured ?? false;

  const copyToken = () => {
    if (!minted) return;
    void navigator.clipboard.writeText(minted).then(() => setCopied(true));
  };

  return (
    <section className="space-y-4 rounded-lg border border-border/60 bg-surface p-5">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <h3 className="text-sm font-medium">Token di servizio di Central</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        La credenziale con cui <strong>Tray Central</strong> interroga la directory del Control
        (membri di un partner, attribuzioni, contatti) per gli atti di sistema — le notifiche. Qui
        viene salvato solo l'<strong>hash</strong>: il token in chiaro appare{" "}
        <strong>una volta sola</strong> alla generazione. Va messo nell'ambiente di Central come{" "}
        <code className="rounded bg-elevated px-1 py-0.5 text-xs">
          CENTRAL_CONTROL_SERVICE_TOKEN
        </code>{" "}
        e applicato con un riavvio. Senza token Central degrada con onestà: le rotte funzionano, le
        notifiche che richiedono la directory tacciono.
      </p>

      {statusQ.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm">
            Stato:{" "}
            {configured ? (
              <span className="font-medium text-success">configurato</span>
            ) : (
              <span className="font-medium text-muted-foreground">non configurato</span>
            )}
          </div>

          {minted && (
            <div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-3">
              <p className="text-xs font-medium text-warning">
                Copia il token ORA — non verrà mai più mostrato.
              </p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all rounded bg-elevated px-2 py-1 font-mono text-xs">
                  {minted}
                </code>
                <Button size="sm" variant="outline" onClick={copyToken}>
                  {copied ? (
                    <Check className="h-4 w-4 text-success" strokeWidth={1.5} />
                  ) : (
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                  )}
                  {copied ? "Copiato" : "Copia"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              disabled={mint.isPending}
              onClick={() => {
                if (
                  configured &&
                  !window.confirm(
                    "Rigenerare il token? Quello attuale smette di funzionare subito: Central va aggiornato col nuovo valore e riavviato.",
                  )
                )
                  return;
                mint.mutate();
              }}
            >
              {mint.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
              {configured ? "Rigenera token" : "Genera token"}
            </Button>
            {configured && (
              <Button
                size="sm"
                variant="outline"
                className="text-danger"
                disabled={revoke.isPending}
                onClick={() => {
                  if (
                    !window.confirm(
                      "Revocare il token? Central perde l'accesso alla directory finché non ne generi uno nuovo.",
                    )
                  )
                    return;
                  revoke.mutate();
                }}
              >
                {revoke.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
                Revoca
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** Platform SMTP relay (ADR-0077): the mail Control uses for identity invites +
 * self-service reset. The password is WRITE-ONLY — the API says only whether one is
 * stored; typing here replaces it, "Rimuovi" clears it, leaving it blank keeps it. */
function SmtpSection() {
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: KEY, queryFn: () => platformApi.getSettings() });

  const [form, setForm] = useState<PlatformSmtpConfig | null>(null);
  const [password, setPassword] = useState("");
  useEffect(() => {
    if (settingsQ.data) {
      setForm({
        smtp_host: settingsQ.data.smtp_host,
        smtp_port: settingsQ.data.smtp_port,
        smtp_tls: settingsQ.data.smtp_tls,
        smtp_username: settingsQ.data.smtp_username,
        smtp_from: settingsQ.data.smtp_from,
      });
      setPassword("");
    }
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: () =>
      platformApi.updateSettings({
        ...(form as PlatformSmtpConfig),
        // Blank = keep the stored password; the explicit clear goes through "Rimuovi".
        ...(password ? { smtp_password: password } : {}),
      }),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      setPassword("");
    },
  });
  const clearPassword = useMutation({
    mutationFn: () => platformApi.updateSettings({ smtp_password: "" }),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });

  const data = settingsQ.data;
  const dirty =
    form != null &&
    data != null &&
    (password !== "" ||
      form.smtp_host !== data.smtp_host ||
      form.smtp_port !== data.smtp_port ||
      form.smtp_tls !== data.smtp_tls ||
      form.smtp_username !== data.smtp_username ||
      form.smtp_from !== data.smtp_from);

  if (!form || !data) return null;
  const set = (patch: Partial<PlatformSmtpConfig>) => setForm({ ...form, ...patch });

  return (
    <section className="space-y-4 rounded-lg border border-border/60 bg-surface p-5">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <h3 className="text-sm font-medium">Email piattaforma (SMTP)</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Il relay che la piattaforma usa per gli <strong>inviti</strong> degli Account Tray e il
        reset password self-service. Senza configurazione le email non partono, ma ogni invito
        produce comunque un <strong>link copiabile</strong> — la posta è la comodità, mai il
        requisito.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block space-y-1">
          <span className="text-sm font-medium">Server SMTP</span>
          <Input
            value={form.smtp_host}
            placeholder="smtp.example.com"
            onChange={(e) => set({ smtp_host: e.target.value })}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Porta</span>
          <Input
            type="number"
            min={1}
            max={65535}
            value={form.smtp_port}
            onChange={(e) => set({ smtp_port: Math.max(1, Number(e.target.value) || 587) })}
            className="max-w-[8rem]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Sicurezza</span>
          <select
            aria-label="Sicurezza SMTP"
            value={form.smtp_tls}
            onChange={(e) => set({ smtp_tls: e.target.value as PlatformSmtpConfig["smtp_tls"] })}
          >
            <option value="starttls">STARTTLS</option>
            <option value="ssl">SSL/TLS</option>
            <option value="none">Nessuna</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Mittente (From)</span>
          <Input
            value={form.smtp_from}
            placeholder="noreply@tuodominio.it"
            onChange={(e) => set({ smtp_from: e.target.value })}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Utente</span>
          <Input
            value={form.smtp_username}
            placeholder="(vuoto = relay senza autenticazione)"
            onChange={(e) => set({ smtp_username: e.target.value })}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium">Password</span>
          <Input
            type="password"
            value={password}
            placeholder={data.smtp_password_set ? "•••••••• (impostata)" : "(non impostata)"}
            onChange={(e) => setPassword(e.target.value)}
          />
          {data.smtp_password_set && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => clearPassword.mutate()}
            >
              Rimuovi password
            </button>
          )}
        </label>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
          Salva SMTP
        </Button>
        {save.isSuccess && !dirty && <span className="text-sm text-success">Salvato.</span>}
      </div>
    </section>
  );
}

function CapField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{label}</span>
      <Input
        type="number"
        min={0}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="max-w-[10rem]"
      />
      <span className="block text-xs text-muted-foreground">{hint}</span>
    </label>
  );
}
